import { cn } from '../../lib/utils'
import { Button } from './Button'

type ErrorVariant = 'inline' | 'banner' | 'card'

interface ErrorMessageProps {
  message: string
  variant?: ErrorVariant
  onRetry?: () => void
  retryText?: string
  className?: string
}

export function ErrorMessage({
  message,
  variant = 'inline',
  onRetry,
  retryText = 'Try Again',
  className,
}: ErrorMessageProps) {
  // Inline variant - simple text with error styling
  if (variant === 'inline') {
    return (
      <div className={cn('text-danger text-sm', className)} role="alert">
        {message}
      </div>
    )
  }

  // Banner variant - prominent banner with icon
  if (variant === 'banner') {
    return (
      <div
        className={cn(
          'bg-danger/10 border border-danger/30 rounded-md p-4',
          className
        )}
        role="alert"
      >
        <div className="flex items-start">
          {/* Error Icon */}
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-danger"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          {/* Message and Action */}
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-danger">{message}</p>
            {onRetry && (
              <div className="mt-3">
                <Button
                  variant="default"
                  size="sm"
                  onClick={onRetry}
                >
                  {retryText}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Card variant - full card with error styling
  return (
    <div
      className={cn(
        'bg-background border-2 border-danger/50 rounded-lg shadow-md p-6',
        className
      )}
      role="alert"
    >
      <div className="flex flex-col items-center text-center">
        {/* Error Icon */}
        <div className="flex-shrink-0 mb-4">
          <svg
            className="h-12 w-12 text-danger"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
        </div>

        {/* Message */}
        <h3 className="text-lg font-semibold text-text mb-2">
          Something went wrong
        </h3>
        <p className="text-sm text-text-muted mb-4">{message}</p>

        {/* Action */}
        {onRetry && (
          <Button variant="primary" onClick={onRetry}>
            {retryText}
          </Button>
        )}
      </div>
    </div>
  )
}

export default ErrorMessage
